import * as Sentry from '@sentry/react';

const defaultEnvs = {
  lang: {
    default: 'es-MX',
  },
  currency: 'MXN',
  defaultPaymentOption: 'credit_card',

  defaultPaymentMethod: {},

  phoneCountryCode: '+52',

  supportAmexCard: true,

  exchangeContactData: {
    phone: '',
    extension: '',
    whatsapp: '',
    email: '',
    whatsappMessage: '',
    fareRestrictionsLink: '',
    termsAndConditionsLink: '',
  },

  installments: {
    availableBanks: [
      'bancomer',
      'hsbc',
      'banorte',
      'banregio',
      'inbursa',
      'scotiabank',
      'afirme',
      'santander',
      'amex',
      'monex',
      'invex',
    ],
    unavailableBanks: [],
  },

  adyen: {
    enabled: false,
  },

  stripe: {
    enabled: false,
    // `enabled` is the only required param for Stripe Express Checkout Element wallets
    // (Google Pay, Apple Pay); they do not need merchantName/merchantId/buttonType/buttonColor
    // like Adyen. Stripe manages the Apple merchant ID.
    googlePay: {
      enabled: false,
    },
    applePay: {
      enabled: false,
    },
  },

  kushki: {
    enabled: false,
  },

  mercadoPago: {
    enabled: false,
  },

  openPay: {
    enabled: false,
    showInFooter: false,
  },

  cielo: {
    enabled: false,
    sopAccessTokenEndpoint: '',
    sopScriptUrl:
      'https://transactionsandbox.pagador.com.br/post/scripts/silentorderpost-1.0.min.js',
    sopEnvironment: 'sandbox',
    sopLanguage: 'en',
    sopEnableBinQuery: true,
    sopEnableVerifyCard: false,
    sopCvvRequired: true,
    enableThreeDSOnRetry: false,
  },

  rede: {
    enabled: false,
    tokenizeEndpoint: '',
  },

  paypal: {
    enabled: false,
    presentationMode: 'auto', // 'auto', 'modal', 'popup', 'redirect'
    paypalRest: {
      environment: 'sandbox',
    },
    sdkUrls: {
      sandbox: 'https://www.sandbox.paypal.com/web-sdk/v6/core',
      production: 'https://www.paypal.com/web-sdk/v6/core',
    },
  },

  errorMonitoring: {
    enabled: false,
  },

  growthBook: {
    enabled: false,
    enableDevMode: false,
  },

  oneSignal: {
    enabled: false,
  },

  taplytics: {
    enabled: false,
  },
  tripPriceDecimals: 0,

  footer: {
    logos: [],
  },

  redirectPartners: {},

  documentTypes: {
    passengers: {},
    passengersSecond: {},
    purchaser: {},
    ignoredDocumentsOnFirstPassenger: [],
  },

  nationalities: [],

  // Recline options available for filtering
  availableReclinations: [],

  // see utils/smartBanner for configuration
  smartBanner: { enabled: false },

  // Promo copy for AppDownloadModal when text2 is set in tenant config.
  // text2 and text4 use primary highlight styling; promo image is bundled in the repo.
  promoDownloadModal: {
    text1: '',
    text2: '',
    text3: '',
    text4: '',
  },

  fullstory: {
    enabled: false,
  },

  segment: {
    token: '',
    options: {},
  },

  conekta: {
    enabled: false,
  },

  sift: {
    enabled: false,
  },

  sentry: {
    dsn: 'https://7cd49be24aad42f689f14883ca288ffd@o25518.ingest.sentry.io/4505518336507904',
    normalizeDepth: 10, // indicates depth of context state captured
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.replayIntegration({
        beforeErrorSampling: (error) => {
          const errorMessages = [
            'evaluating n[t.watch]',
            'OneSignal',
            'Failed to fetch',
            "reading 'state'",
          ];
          // Sentry will still capture these errors as events; this only skips
          // attaching a session replay recording for them.
          return !errorMessages.some((msg) => error.message?.includes(msg));
        },
      }),
    ],
    options: {
      release: __RELEASE__,
      debug: __DEV__,
      environment: __PROD__ ? 'prod' : 'sandbox',
      sampleRate: __PROD__ ? 1 : 0.1,
      ignoreErrors: [
        // Random plugins/extensions
        'top.GLOBALS',
        // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error. html
        'originalCreateNotification',
        'canvas.contentDocument',
        'MyApp_RemoveAllHighlights',
        'http://tt.epicplay.com',
        "Can't find variable: ZiteReader",
        "Can't find variable: chrome",
        'jigsaw is not defined',
        'ComboSearch is not defined',
        'http://loading.retry.widdit.com/',
        'atomicFindClose',
        'vid_mate_check',
        // Android webview erros
        'androidInterface is not defined',
        'Java exception was raised during method invocation',
        'androidOnPause is not defined',
        // Facebook borked
        'fb_xd_fragment',
        // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
        // reduce this. (thanks @acdha)
        // See http://stackoverflow.com/questions/4113268
        'bmi_SafeAddOnload',
        'EBCallBackMessageReceived',
        // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
        'conduitPage',
        // See: https://bugs.chromium.org/p/chromium/issues/detail?id=590375
        "undefined is not an object (evaluating '__gCrWeb.autofill.extractForms')",
        // ZTE devices bugs
        'zte',
        // Firefox erros
        '__firefox__',
        // Conekta iframe error
        'https://ssl.kaptcha.com',
        // Ad scripts
        'https://bid.g.doubleclick.net',
        // https://github.com/bugsnag/bugsnag-js/issues/186
        '__show__deepen is not defined',
        'Object Not Found Matching Id',
        // Riskified
        "Cannot read properties of null (reading 'riskified_cookie')",
        'Object captured as exception with keys: code, message',
        'No fingerprint id',
      ],
      denyUrls: [
        // Facebook flakiness
        /graph\.facebook\.com/i,
        // Facebook blocked
        /connect\.facebook\.net\/en_US\/all\.js/i,
        // Woopra flakiness
        /eatdifferent\.com\.woopra-ns\.com/i,
        /static\.woopra\.com\/js\/woopra\.js/i,
        // Chrome extensions
        /extensions\//i,
        /^chrome:\/\//i,
        // Other plugins
        /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
        /webappstoolbarba\.texthelp\.com\//i,
        /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
      ],
    },
  },
  gtmContainerId: 'GTM-MHZZBN2',
  cookiebot: {
    enabled: false, // If true, loads the Cookiebot consent manager wired to Google Consent Mode (replaces PrivacyBanner where enabled).
    appId: '', // Cookiebot Domain Group ID (data-cbid). Set per tenant.
  },
  travelTipsAllowedRoutes: [],
  accommodationWhatsappUrl: '', // WhatsApp URL for the accommodation promo link on the purchase complete screen
  showPricesByGroupConfig: {
    enabledOnAllRoutes: false, // If true, shows the prices by group on all routes
    // If enabledOnAllRoutes is false, set routes here or in tenant whitelabel env (bidirectional match).
    allowedRoutes: [],
  },
};

export default defaultEnvs;
